/**
 * Campaign extensions — the request/approve pipeline for the one 30-day
 * extension each campaign is allowed under the current platform_policy.
 *
 * Life of a request:
 *   1. Student clicks "Request extension" on their campaign detail page.
 *   2. A record is written here in `pending` state with the reason text.
 *   3. Admin reviews in AdminExtensions and either:
 *      - approves → status "approved", new end date computed from the
 *                   campaign's original end + policy.extensionDays
 *      - denies   → status "denied", stays for the audit trail
 *   4. Public campaign detail reflects the new end date once approved
 *      (via `getApprovedExtensionForCampaign`).
 *
 * Policy contract (read live from lib/platformPolicy.js):
 *   - `extensionsAllowed`         → max # of extensions per campaign
 *   - `extensionDays`             → days added on approval
 *   - `extensionRequiresAdminApproval` → currently always true
 *
 * State survives page reloads via localStorage. Swap the four exports
 * for API calls when the backend arrives; keep the same shape so no
 * consumer needs to change.
 */

import { getPolicy } from "./platformPolicy";

const STORAGE_KEY = "mra.campaignExtensions.v1";

const SEED = [
  // A pending request on Andre's campaign — gives the admin queue
  // something to look at on first load.
  {
    id: "ext-100",
    campaignId: 1,
    requestedAt: "2026-02-25T15:20:00Z",
    requesterName: "Andre",
    reason:
      "The second-round interview at the summer research lab was rescheduled to late May, and my move-in essentials budget still has a gap for a printer and interview attire. Thirty extra days would bring my page in line with the interview timing.",
    status: "pending",
  },
];

/* -------------------------------------------------------------------------- */
/* Storage helpers                                                            */
/* -------------------------------------------------------------------------- */

const listeners = new Set();

const read = () => {
  if (typeof window === "undefined") return { extensions: [...SEED] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { extensions: [...SEED] };
    const parsed = JSON.parse(raw);
    return { extensions: Array.isArray(parsed.extensions) ? parsed.extensions : [...SEED] };
  } catch {
    return { extensions: [...SEED] };
  }
};

const write = (store) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore in prototype */
  }
  listeners.forEach((cb) => cb(store));
};

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

export const getAllExtensions = () => {
  const { extensions } = read();
  return [...extensions].sort(
    (a, b) => new Date(b.requestedAt) - new Date(a.requestedAt),
  );
};

export const getPendingExtensions = () => {
  const { extensions } = read();
  return extensions
    .filter((e) => e.status === "pending")
    .sort((a, b) => new Date(a.requestedAt) - new Date(b.requestedAt));
};

export const getExtensionsForCampaign = (campaignId) => {
  const { extensions } = read();
  return extensions.filter((e) => e.campaignId === campaignId);
};

// Convenience: is there an approved extension for a campaign?
export const getApprovedExtensionForCampaign = (campaignId) => {
  const { extensions } = read();
  return extensions.find((e) => e.campaignId === campaignId && e.status === "approved") || null;
};

// Convenience: is there a pending request for a campaign?
export const getPendingExtensionForCampaign = (campaignId) => {
  const { extensions } = read();
  return extensions.find((e) => e.campaignId === campaignId && e.status === "pending") || null;
};

// Returns { canRequest, reason } based on live policy + prior requests
// for a campaign. Used by the student-side UI to decide whether to show
// the request button.
export const canRequestExtension = (campaignId) => {
  const policy = getPolicy();
  const priors = getExtensionsForCampaign(campaignId);
  const used = priors.filter((e) => e.status === "approved").length;
  const pending = priors.find((e) => e.status === "pending");

  if (pending) {
    return { canRequest: false, reason: "You already have a pending extension request." };
  }
  if (used >= (policy.extensionsAllowed ?? 1)) {
    return { canRequest: false, reason: `The maximum of ${policy.extensionsAllowed} extension has already been approved for this campaign.` };
  }
  return { canRequest: true, reason: null };
};

export const requestExtension = ({ campaignId, requesterName, reason }) => {
  const { extensions } = read();
  const id = `ext-${Date.now()}`;
  const next = {
    extensions: [
      ...extensions,
      {
        id,
        campaignId,
        requestedAt: new Date().toISOString(),
        requesterName: requesterName || "Student",
        reason: (reason || "").trim(),
        status: "pending",
      },
    ],
  };
  write(next);
  return id;
};

export const approveExtension = (id, moderatorNote = "") => {
  const { extensions } = read();
  const policy = getPolicy();
  const updated = extensions.map((e) =>
    e.id === id
      ? {
          ...e,
          status: "approved",
          decidedAt: new Date().toISOString(),
          moderatorNote,
          daysAdded: policy.extensionDays,
        }
      : e,
  );
  write({ extensions: updated });
};

export const denyExtension = (id, moderatorNote = "") => {
  const { extensions } = read();
  const updated = extensions.map((e) =>
    e.id === id
      ? {
          ...e,
          status: "denied",
          decidedAt: new Date().toISOString(),
          moderatorNote,
        }
      : e,
  );
  write({ extensions: updated });
};

export const subscribeExtensions = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
